<template>
  <button
    type="button"
    :disabled="disabled || isLoading"
    :class="buttonClass"
    :style="{ '--button-loader-color': 'currentColor' }"
  >
    <span :class="isLoading ? 'tw:invisible' : ''"><slot /></span>
    <span
      v-if="isLoading"
      class="tw:absolute! tw:inset-0 tw:flex tw:items-center tw:justify-center"
    >
      <span
        class="tw:inline-flex tw:items-center tw:gap-1.5"
        aria-hidden="true"
      >
        <span :class="dotClass"></span>
        <span :class="dotClass"></span>
        <span :class="dotClass"></span>
      </span>
    </span>
  </button>
</template>

<script setup lang="ts">
type Size = 'big' | 'large' | 'medium' | 'small' | 'tiny'
type Kind = 'fill' | 'weak'
type Color = 'primary' | 'red' | 'dark' | 'white'

export type CtaButtonProps = {
  size?: Size
  type?: Kind
  color?: Color
  disabled?: boolean
  isLoading?: boolean
  customClass?: string
}

const props = withDefaults(defineProps<CtaButtonProps>(), {
  size: 'medium',
  type: 'fill',
  color: 'primary',
  disabled: false,
  isLoading: false,
  customClass: ''
})

const colorStyles: Record<Kind, Record<Color, { bg: string; text: string; border?: string }>> = {
  fill: {
    primary: {
      bg: 'tw:bg-usdg-color-button-primary-bg',
      text: 'tw:text-usdg-color-button-primary-text'
    },
    red: {
      bg: 'tw:bg-usdg-color-button-primary-bg-red',
      text: 'tw:text-usdg-color-button-primary-text-white'
    },
    dark: {
      bg: 'tw:bg-usdg-color-button-primary-bg-gray',
      text: 'tw:text-usdg-color-button-primary-text-white'
    },
    white: {
      bg: 'tw:bg-usdg-color-gray-0',
      text: 'tw:text-usdg-color-gray-700',
      border: 'tw:border-usdg-color-border-medium'
    }
  },
  weak: {
    primary: {
      bg: 'tw:bg-usdg-color-button-secondary-bg-blue',
      text: 'tw:text-usdg-color-button-secondary-text-blue'
    },
    red: { bg: 'tw:bg-usdg-color-red-050', text: 'tw:text-usdg-color-red-500' },
    dark: { bg: 'tw:bg-usdg-color-gray-100', text: 'tw:text-usdg-color-gray-700' },
    white: { bg: 'tw:bg-usdg-color-gray-050', text: 'tw:text-usdg-color-gray-500' }
  }
}

const sizeMap: Record<Size, string> = {
  big: 'tw:py-4 tw:px-8 tw:typo-text-l tw:rounded-16',
  large: 'tw:py-3 tw:px-4 tw:typo-text-l tw:rounded-14',
  medium: 'tw:py-2.5 tw:px-5 tw:typo-text-m tw:rounded-10',
  small: 'tw:py-1.5 tw:px-4 tw:typo-text-m tw:rounded-8',
  tiny: 'tw:py-1.5 tw:px-3 tw:typo-text-s tw:rounded-8'
}

const base =
  'tw:relative tw:inline-flex tw:items-center tw:justify-center tw:font-semibold tw:select-none tw:whitespace-nowrap tw:transition tw:disabled:pointer-events-none tw:cursor-pointer cta-overlay'

const dotClass = 'tw:h-1.5 tw:w-1.5 tw:rounded-full cta-dot'

const buttonClass = computed(() => {
  const styles = colorStyles[props.type][props.color]

  return [
    base,
    sizeMap[props.size],
    styles.bg,
    styles.text,
    styles.border ?? '',
    props.disabled && 'tw:opacity-40',
    props.isLoading && 'cta-overlay--loading',
    props.customClass
  ]
})
</script>

<style scoped>
.cta-overlay::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background-color: var(--tw-color-usdg-color-gray-900);
  opacity: 0;
}

.cta-overlay:active::before {
  opacity: 0.1;
}

.cta-overlay--loading::before {
  opacity: 0.2;
}

.cta-dot {
  background-color: var(--button-loader-color);
  animation: loading-dot 1.2s infinite ease-in-out;
}

.cta-dot:nth-child(1) {
  animation-delay: -0.2s;
}

.cta-dot:nth-child(2) {
  animation-delay: -0.1s;
}
</style>

<style>
@keyframes loading-dot {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>
